import React from 'react';
import { Alert, Card, Typography, List } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface ConfigErrorProps {
  missingFields: string[];
}

const isDevelopment = import.meta.env.DEV;
/**
 * Production error screen - shows generic message without exposing internal config
 */
function ProductionError() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        padding: 24
      }}
    >
      <Card style={{ maxWidth: 500, width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <WarningOutlined style={{ fontSize: 64, color: '#faad14' }} />
          <Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
            Service Unavailable
          </Title>
          <Paragraph type="secondary">
            The application is temporarily unavailable. Please try again later or contact support if the issue persists.
          </Paragraph>
        </div>
      </Card>
    </div>
  );
}

/**
 * Development error screen - shows detailed configuration instructions
 */
function DevelopmentError({ missingFields }: ConfigErrorProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        padding: 24
      }}
    >
      <Card style={{ maxWidth: 600, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <WarningOutlined style={{ fontSize: 64, color: '#faad14' }} />
          <Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
            Configuration Required
          </Title>
          <Paragraph type="secondary">
            The application cannot start because required configuration is missing.
          </Paragraph>
        </div>

        <Alert
          type="error"
          message="Missing Configuration"
          description={
            <List
              size="small"
              dataSource={missingFields}
              renderItem={(field) => (
                <List.Item>
                  <Text code>{field}</Text> is not configured
                </List.Item>
              )}
            />
          }
          style={{ marginBottom: 24 }}
        />

        <Title level={5}>How to fix:</Title>

        <Paragraph>
          <Text strong>Option 1: Runtime Configuration</Text>
          <br />
          Create a <Text code>/public/env.json</Text> file with your configuration:
        </Paragraph>

        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: 16,
            borderRadius: 4,
            overflow: 'auto'
          }}
        >
          {`{
  "ADMIN_API_URL": "https://your-api-url.com",
  "TURNSTILE_SITE_KEY": "your-turnstile-key",
  "SUPPORT_CENTER_BUTTON_ENABLED": "false",
  "ENABLE_DEVELOPER_CONSOLE": "false",
  "ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES": "false"
}`}
        </pre>

        <Paragraph style={{ marginTop: 16 }}>
          <Text strong>Option 2: Build-time Configuration</Text>
          <br />
          Set environment variables before building:
        </Paragraph>

        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: 16,
            borderRadius: 4,
            overflow: 'auto'
          }}
        >
          {`# In .env.local or shell
VITE_ADMIN_API_URL=https://your-api-url.com
VITE_TURNSTILE_SITE_KEY=your-turnstile-key`}
        </pre>

        <Paragraph type="secondary" style={{ marginTop: 24 }}>
          After configuring, refresh this page to continue.
        </Paragraph>
      </Card>
    </div>
  );
}

/**
 * Error screen shown when required configuration is missing
 * - Development: Shows detailed configuration instructions
 * - Production: Shows generic error message (no internal details exposed)
 */
export function ConfigError({ missingFields }: ConfigErrorProps) {
  if (isDevelopment) {
    return <DevelopmentError missingFields={missingFields} />;
  }

  return <ProductionError />;
}

export default ConfigError;
